import { LocalStorageCachedDataKeysHMI } from "constants/constants";
import { useCallback, useContext, useEffect, useRef } from "react";
import { ManualQualityMQTTEntity } from "../mqtt/entities/ManualQuality/ManualQualityEntity";
import {
    IManualQualityRequestPayload,
    IManualQualityResponsePayload,
} from "../mqtt/entities/ManualQuality/ManualQualityInterfaces";
import { HMICachedDataContext } from "../contexts/HMICachedDataContext/HMICachedDataContext";
import { buildManualQualityPendingKey } from "../contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { MQTTBrokerContext } from "../contexts/MQTTBrokerContext/MQTTBrokerContext";

const MAX_REPLAY_ATTEMPTS = 5;

/**
 * Interval (ms) for the periodic safety-net drain. The reconnect-transition
 * drain is edge-triggered — it fires once and, on a publish failure, stops
 * until the next transition. This level-triggered retry keeps draining as
 * long as there is queued work and the local-server is active.
 */
const MANUAL_QUALITY_RETRY_INTERVAL_MS = 30_000;

/**
 * Thrown by `submit()` when the local-server is known-disconnected at
 * submit time. The entry is already persisted to the queue (attempts: 0)
 * and the optimistic overlay is shown — `drainQueue` replays it on the
 * next reconnect. Callers treat this exactly like a publish failure
 * (queued → close modal); the distinct type only enables an accurate log
 * line and signals "we never attempted" vs. an unexpected throw.
 */
export class QueuedForReplayError extends Error {
    constructor() {
        super("Local-server unreachable — queued for replay");
        this.name = "QueuedForReplayError";
    }
}

export interface IQueuedManualQualitySubmission extends IManualQualityRequestPayload {
    /** Wall-clock ms when the submission was first enqueued. */
    enqueuedAt: number;
    /** Number of replay attempts so far; bounds the retry storm. */
    attempts: number;
}

const readQueue = (): IQueuedManualQualitySubmission[] => {
    try {
        const raw = localStorage.getItem(LocalStorageCachedDataKeysHMI.MANUAL_QUALITY_QUEUE);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeQueue = (queue: IQueuedManualQualitySubmission[]): void => {
    try {
        localStorage.setItem(LocalStorageCachedDataKeysHMI.MANUAL_QUALITY_QUEUE, JSON.stringify(queue));
    } catch {
        // localStorage may be full or unavailable; silently drop — the operator
        // sees the modal's submit error and can re-submit.
    }
};

export interface IUseManualQualityQueue {
    /**
     * Enqueue a submission and try to send it now if the local-server is
     * reachable. Resolves with the response on success; rejects if the
     * publish fails (the entry stays queued for replay).
     */
    submit(
        payload: Omit<IManualQualityRequestPayload, "uuid" | "timestamp"> & { uuid?: string; timestamp?: number },
    ): Promise<IManualQualityResponsePayload>;
    /** Inspect the queue (for surfacing "pending" badges, debugging, etc.). */
    peekAll(): IQueuedManualQualitySubmission[];
}

const generateUuid = (): string => {
    if (typeof crypto !== "undefined" && typeof (crypto as Crypto & { randomUUID?: () => string }).randomUUID === "function") {
        return (crypto as Crypto & { randomUUID: () => string }).randomUUID();
    }
    // Lightweight fallback for older browsers — still server-validated by uuid presence.
    return `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Owns the localStorage-backed offline queue for manual scrap submissions.
 *
 * Behavior:
 *  - `submit()` enqueues the entry, attempts an immediate publish, and
 *    dequeues on success. On failure the entry stays in the queue and the
 *    promise rejects so the caller can show an inline "queued" hint.
 *  - On every transition of `isLocalServerActive` from false → true we drain
 *    the queue sequentially (oldest first), bounded by MAX_REPLAY_ATTEMPTS
 *    per entry. Successful entries are removed; entries that exceed the
 *    attempt cap are also removed (logged) to prevent unbounded growth.
 *  - The same `uuid` is reused across replay attempts so the local-server
 *    idempotency cache short-circuits duplicates.
 */
export const useManualQualityQueue = (): IUseManualQualityQueue => {
    const { isLocalServerActive } = useContext(MQTTBrokerContext);
    const { dispatch } = useContext(HMICachedDataContext);
    // Initialized to `false` (not the current value) so that "active at
    // mount" counts as a false→true transition and drains the queue once on
    // mount — closes the gap where an entry stranded by a dropped-after-
    // timeout response would otherwise wait for an unrelated reconnect.
    const wasActiveRef = useRef<boolean>(false);
    const drainingRef = useRef<boolean>(false);
    // Mirrors `isLocalServerActive` for reads inside `submit()` without
    // churning its `useCallback` identity. Written during render (no
    // effect-lag staleness window).
    const isLocalServerActiveRef = useRef<boolean>(isLocalServerActive);
    isLocalServerActiveRef.current = isLocalServerActive;

    const drainQueue = useCallback(async () => {
        if (drainingRef.current) {
            return;
        }
        drainingRef.current = true;
        try {
            let queue = readQueue();
            while (queue.length > 0) {
                const entry = queue[0];
                const pendingKey = buildManualQualityPendingKey(entry.machineCenterId, entry.intervalStartIso);
                if (entry.attempts >= MAX_REPLAY_ATTEMPTS) {
                    queue = queue.slice(1);
                    writeQueue(queue);
                    // Drop the matching pending overlay too — operator shouldn't
                    // keep seeing an indicator for a submission we've given up on.
                    dispatch.clearPendingManualQuality(pendingKey);
                    // tslint:disable-next-line: no-console
                    console.error(
                        `[useManualQualityQueue] dropping entry after ${MAX_REPLAY_ATTEMPTS} failed attempts`,
                        entry,
                    );
                    continue;
                }
                try {
                    const response = await ManualQualityMQTTEntity.publishManualQuality(entry);
                    if (response.success) {
                        queue = readQueue().filter((q) => q.uuid !== entry.uuid);
                        writeQueue(queue);
                        dispatch.markPendingManualQualityAcked(pendingKey);
                    } else {
                        const updated = readQueue().map((q) =>
                            q.uuid === entry.uuid ? { ...q, attempts: q.attempts + 1 } : q,
                        );
                        writeQueue(updated);
                        queue = updated;
                        // Server explicitly rejected: drop pending overlay (the value
                        // is not going to land); operator may resubmit fresh.
                        dispatch.clearPendingManualQuality(pendingKey);
                        if (entry.attempts + 1 < MAX_REPLAY_ATTEMPTS) {
                            // tslint:disable-next-line: no-console
                            console.warn(
                                `[useManualQualityQueue] server rejected entry: ${response.error ?? "unknown"}`,
                            );
                        }
                    }
                } catch (err) {
                    const updated = readQueue().map((q) =>
                        q.uuid === entry.uuid ? { ...q, attempts: q.attempts + 1 } : q,
                    );
                    writeQueue(updated);
                    queue = updated;
                    // Stop draining on a publish error — wait for the next active transition.
                    // Pending overlay STAYS — the queue will retry, and the operator
                    // continues to see the optimistic value.
                    break;
                }
            }
        } finally {
            drainingRef.current = false;
        }
    }, [dispatch]);

    useEffect(() => {
        const wasActive = wasActiveRef.current;
        wasActiveRef.current = isLocalServerActive;
        if (!wasActive && isLocalServerActive) {
            void drainQueue();
        }
    }, [isLocalServerActive, drainQueue]);

    // Periodic safety-net drain. The transition effect above is edge-triggered:
    // it fires once on reconnect, and if that single drain's publish fails it
    // `break`s and waits for the *next* transition — which may never come on a
    // stable connection, stranding the entry. This interval is level-triggered:
    // as long as there is queued work AND the local-server is active, it keeps
    // retrying. `drainQueue`'s `drainingRef` guard makes overlapping ticks safe.
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (isLocalServerActiveRef.current && readQueue().length > 0) {
                void drainQueue();
            }
        }, MANUAL_QUALITY_RETRY_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [drainQueue]);

    const submit = useCallback(
        async (
            input: Omit<IManualQualityRequestPayload, "uuid" | "timestamp"> & { uuid?: string; timestamp?: number },
        ): Promise<IManualQualityResponsePayload> => {
            const payload: IManualQualityRequestPayload = {
                intervalStartIso: input.intervalStartIso,
                isMachineDown: input.isMachineDown,
                machineCenterId: input.machineCenterId,
                plcAddress: input.plcAddress,
                timestamp: input.timestamp ?? Date.now(),
                uuid: input.uuid ?? generateUuid(),
                value: input.value,
            };
            const queued: IQueuedManualQualitySubmission = {
                ...payload,
                attempts: 0,
                enqueuedAt: Date.now(),
            };
            const existing = readQueue().filter((q) => q.uuid !== payload.uuid);
            writeQueue([...existing, queued]);

            const pendingKey = buildManualQualityPendingKey(payload.machineCenterId, payload.intervalStartIso);
            // Track pending BEFORE the publish attempt so the optimistic UI works
            // even when offline. Lifecycle: mqttAcked flips on success, sync clears.
            dispatch.trackPendingManualQuality({
                hourAlignedIntervalStartIso: payload.intervalStartIso,
                machineCenterId: payload.machineCenterId,
                mqttAcked: false,
                submittedAt: payload.timestamp,
                syncObserved: false,
                uuid: payload.uuid,
                value: payload.value,
            });

            if (!isLocalServerActiveRef.current) {
                // Known-disconnected: skip the doomed publish + timeout wait.
                // The entry is already queued (attempts: 0) and the overlay is
                // shown; drainQueue replays it on reconnect. Throw so the
                // caller takes the same "queued → close modal" path as a
                // publish failure — but do NOT bump `attempts`, we never
                // actually attempted.
                throw new QueuedForReplayError();
            }

            try {
                const response = await ManualQualityMQTTEntity.publishManualQuality(payload);
                if (response.success) {
                    const remaining = readQueue().filter((q) => q.uuid !== payload.uuid);
                    writeQueue(remaining);
                    dispatch.markPendingManualQualityAcked(pendingKey);
                } else {
                    // Server rejected (gate failed, validation, etc.): drop pending
                    // overlay. The modal surfaces the error inline.
                    dispatch.clearPendingManualQuality(pendingKey);
                }
                return response;
            } catch (err) {
                // Bump the attempt counter so replay logic respects the bound.
                const updated = readQueue().map((q) =>
                    q.uuid === payload.uuid ? { ...q, attempts: q.attempts + 1 } : q,
                );
                writeQueue(updated);
                // Pending overlay STAYS — queue replay will retry and ack later.
                throw err;
            }
        },
        [dispatch],
    );

    const peekAll = useCallback(() => readQueue(), []);

    return { peekAll, submit };
};
